import React, { useEffect, useState } from 'react';
import { CustomModal } from '../CustomModal';
import { Btn } from '../ui';
import '../../css/ResourceLibraryContent.css';

interface DeletePlanModalProps {
    isOpen: boolean;
    onClose: () => void;
    onDelete: () => Promise<void>;
    planName: string;
    standard: string;
    categoryCount: number;
    topicCount: number;
    assignmentCount: number;
}

export const DeletePlanModal: React.FC<DeletePlanModalProps> = ({
    isOpen,
    onClose,
    onDelete,
    planName,
    standard,
    categoryCount,
    topicCount,
    assignmentCount,
}) => {
    const [confirmed, setConfirmed] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmReady, setConfirmReady] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setConfirmReady(false);
            return;
        }
        setConfirmReady(false);
        const timer = window.setTimeout(() => setConfirmReady(true), 400);
        return () => window.clearTimeout(timer);
    }, [isOpen, planName]);

    const handleDelete = async () => {
        setDeleting(true);
        setError(null);
        try {
            await onDelete();
        } catch {
            setError('Failed to delete. Please try again.');
        } finally {
            setDeleting(false);
        }
    };

    const handleClose = () => {
        setConfirmed(false);
        setError(null);
        onClose();
    };

    return (
        <CustomModal
            visible={isOpen}
            onDismiss={handleClose}
            header="Delete Plan Template"
            bodyClassName="zrl-dialog-body"
            footer={
                <div className="modal-footer-actions">
                    <Btn
                        variant="secondary-outlined"
                        buttonText="Cancel"
                        buttonDisabled={deleting}
                        buttonOnClick={handleClose}
                    />
                    <Btn
                        variant="danger"
                        buttonText={deleting ? 'Deleting...' : 'Delete Plan Template'}
                        buttonDisabled={!confirmed || deleting || !confirmReady}
                        buttonOnClick={handleDelete}
                    />
                </div>
            }
        >
                    {/* Warning Banner */}
                    <div className="zrl-warning-banner">
                        <span className="zrl-warning-icon">⚠️</span>
                        <span>
                            This will permanently delete the plan template. Company copies that were
                            already created are <strong>NOT</strong> affected — they remain as independent
                            local copies. Shared topic templates are also preserved.
                        </span>
                    </div>

                    {/* Details */}
                    <div className="zrl-delete-details">
                        <div className="zrl-delete-detail">
                            <span className="zrl-delete-detail-label">Plan Name</span>
                            <span className="zrl-delete-detail-value">{planName}</span>
                        </div>
                        <div className="zrl-delete-detail">
                            <span className="zrl-delete-detail-label">Standard</span>
                            <span className="zrl-delete-detail-value">{standard}</span>
                        </div>
                        <div className="zrl-delete-detail">
                            <span className="zrl-delete-detail-label">Categories</span>
                            <span className="zrl-delete-detail-value">{categoryCount}</span>
                        </div>
                        <div className="zrl-delete-detail">
                            <span className="zrl-delete-detail-label">Topics</span>
                            <span className="zrl-delete-detail-value">{topicCount}</span>
                        </div>
                        <div className="zrl-delete-detail">
                            <span className="zrl-delete-detail-label">Assigned Companies</span>
                            <span className="zrl-delete-detail-value">{assignmentCount}</span>
                        </div>
                    </div>

                    {/* Confirmation Checkbox */}
                    <label className="zrl-confirm-checkbox" onClick={() => setConfirmed(!confirmed)}>
                        <input
                            type="checkbox"
                            checked={confirmed}
                            onChange={(e) => setConfirmed(e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                        />
                        <span className="zrl-confirm-text">
                            I understand this will permanently delete <strong>{planName}</strong>.
                            Company copies and shared topics will not be affected.
                        </span>
                    </label>

                    {error && (
                        <div className="zrl-warning-banner" style={{ marginTop: 12 }}>
                            <span className="zrl-warning-icon">⚠️</span>
                            <span>{error}</span>
                        </div>
                    )}
        </CustomModal>
    );
};
